<template>
  <div class="poll-feature-container" :class="spaceLevel">
    <!-- Animated background elements -->
    <div class="background-elements">
      <div class="bg-circle circle-1"></div>
      <div class="bg-circle circle-2"></div>
      <div class="progress-ring-bg"></div>
    </div>
    
    <div class="poll-content">
      <div class="poll-inner">
        <!-- Poll icon with animation -->
        <div class="poll-icon-container">
          <div class="poll-icon">
            <i class="fas fa-poll"></i>
          </div>
          <div class="vote-count-ring" v-if="hasVoted">
            <svg class="progress-ring" width="60" height="60">
              <circle
                class="progress-ring-circle"
                stroke="rgba(255, 255, 255, 0.3)"
                stroke-width="3"
                fill="transparent"
                r="27"
                cx="30"
                cy="30"
              />
              <circle
                class="progress-ring-progress"
                stroke="#ff6b6b"
                stroke-width="3"
                fill="transparent"
                r="27"
                cx="30"
                cy="30"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="circumference - (totalVotes / 100) * circumference"
              />
            </svg>
          </div>
        </div>
        
        <h5 class="poll-title">{{ ad.title }}</h5>
        <p class="poll-question">{{ ad.question }}</p>
        
        <div class="poll-options">
          <div
            v-for="(option, optionIndex) in ad.options"
            :key="optionIndex"
            class="poll-option"
            :class="{ 
              'selected': selectedOption === optionIndex, 
              'disabled': hasVoted,
              'winner': hasVoted && option.percentage === highestPercentage
            }"
            @click.stop="selectOption(optionIndex)"
          >
            <div class="option-content">
              <div class="option-left">
                <div class="option-indicator"></div>
                <span class="option-text">{{ option.text }}</span>
              </div>
              <span v-if="hasVoted" class="option-percentage">{{ option.percentage }}%</span>
            </div>
            <div v-if="hasVoted" class="option-bar">
              <div 
                class="option-fill" 
                :style="{ width: animatedPercentages[optionIndex] + '%' }"
                :class="{ 'winner-fill': option.percentage === highestPercentage }"
              ></div>
            </div>
          </div>
        </div>
        
        <div class="poll-footer">
          <button
            v-if="!hasVoted && selectedOption !== null"
            @click.stop="submitVote"
            class="poll-submit-btn"
          >
            <i class="fas fa-vote-yea"></i>
            <span>Cast Vote</span>
          </button>
          <div v-if="hasVoted" class="poll-completion">
            <i class="fas fa-check-circle"></i>
            <span class="poll-votes">Thank you for voting!</span>
          </div>
        </div>
      </div>
      
      <!-- Glassmorphism overlay -->
      <div class="glass-overlay"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PollAd",
  props: {
    ad: {
      type: Object,
      required: true
    },
    spaceLevel: {
      type: String,
      default: 'normal'
    }
  },
  data() {
    return {
      selectedOption: null,
      hasVoted: false,
      animatedPercentages: [],
      totalVotes: 100,
      circumference: 2 * Math.PI * 27,
      animationFrameIds: [] // Track animation frame IDs for cleanup
    };
  },
  computed: {
    highestPercentage() {
      if (!this.hasVoted || !this.ad.options.length) return 0;
      return Math.max(...this.ad.options.map(opt => opt.percentage || 0));
    }
  },
  mounted() {
    // Check if user has already voted on this poll
    const votedPolls = JSON.parse(localStorage.getItem('votedPolls') || '[]');
    this.hasVoted = votedPolls.includes(this.ad.val);
    
    // Emit loaded event for sizing
    this.$emit('pollLoaded', {
      aspectRatio: 1.2, // Slightly taller than square to accommodate options
      type: 'poll' // Use poll type for dynamic sizing
    });
  },
  methods: {
    selectOption(optionIndex) {
      if (!this.hasVoted) {
        this.selectedOption = optionIndex;
      }
    },
    submitVote() {
      if (this.selectedOption !== null && !this.hasVoted) {
        // Mark as voted
        this.hasVoted = true;
        
        // Store vote in localStorage
        const votedPolls = JSON.parse(localStorage.getItem('votedPolls') || '[]');
        votedPolls.push(this.ad.val);
        localStorage.setItem('votedPolls', JSON.stringify(votedPolls));
        
        // Here you would typically send the vote to your backend
        // For now, we'll just show mock results
        this.showResults();
      }
    },
    showResults() {
      // Mock results - in real implementation, this would come from backend
      this.ad.options.forEach((option, index) => {
        if (index === this.selectedOption) {
          option.percentage = Math.floor(Math.random() * 40) + 30; // 30-70%
        } else {
          option.percentage = Math.floor(Math.random() * 30) + 10; // 10-40%
        }
      });
      
      // Normalize percentages to 100%
      const total = this.ad.options.reduce((sum, opt) => sum + opt.percentage, 0);
      this.ad.options.forEach(option => {
        option.percentage = Math.round((option.percentage / total) * 100);
      });
      
      // Initialize animated percentages at 0
      this.animatedPercentages = new Array(this.ad.options.length).fill(0);
      
      // Animate percentages with staggered timing
      this.ad.options.forEach((option, index) => {
        setTimeout(() => {
          this.animatePercentage(index, option.percentage);
        }, index * 200); // 200ms delay between each option
      });
    },
    
    animatePercentage(index, targetPercentage) {
      let currentPercentage = 0;
      const increment = targetPercentage / 30; // 30 frames for smooth animation
      
      const animateFrame = () => {
        currentPercentage += increment;
        if (currentPercentage >= targetPercentage) {
          this.$set(this.animatedPercentages, index, targetPercentage);
          // Remove completed animation from tracking
          const frameIndex = this.animationFrameIds.findIndex(item => item.index === index);
          if (frameIndex > -1) {
            this.animationFrameIds.splice(frameIndex, 1);
          }
        } else {
          this.$set(this.animatedPercentages, index, Math.round(currentPercentage));
          const frameId = requestAnimationFrame(animateFrame);
          // Update the frame ID for this animation
          const existingIndex = this.animationFrameIds.findIndex(item => item.index === index);
          if (existingIndex > -1) {
            this.animationFrameIds[existingIndex].frameId = frameId;
          } else {
            this.animationFrameIds.push({ index, frameId });
          }
        }
      };
      
      const frameId = requestAnimationFrame(animateFrame);
      this.animationFrameIds.push({ index, frameId });
    },
    
    cleanupAnimations() {
      // Cancel any running animations
      this.animationFrameIds.forEach(({ frameId }) => {
        cancelAnimationFrame(frameId);
      });
      this.animationFrameIds = [];
    }
  },
  beforeDestroy() {
    // Clean up animations when component is destroyed
    this.cleanupAnimations();
  }
};
</script>

<style scoped>
.poll-feature-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.poll-content {
  width: 100%;
  padding: clamp(18px, 3vw, 28px);
  background: linear-gradient(135deg, #ff7675 0%, #fd79a8 50%, #e17055 100%);
  background-size: 200% 200%;
  animation: gradientShift 10s ease-in-out infinite;
  border-radius: 16px;
  color: white;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  container-type: inline-size;
  position: relative;
  box-shadow: 
    0 12px 36px rgba(255, 118, 117, 0.4),
    0 6px 18px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.poll-inner {
  width: 100%;
}

.poll-title {
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 800;
  margin: 0 0 clamp(12px, 1.5vw, 16px) 0;
  text-align: center;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.02em;
  animation: slideInFromTop 0.8s ease-out 0.3s both;
}

.poll-question {
  font-size: clamp(15px, 2.3vw, 20px);
  margin: 0 0 clamp(20px, 2.5vw, 24px) 0;
  text-align: center;
  opacity: 0.95;
  line-height: 1.5;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  animation: slideInFromBottom 0.8s ease-out 0.5s both;
}

.poll-options {
  margin-bottom: 20px;
}

.poll-option {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: clamp(12px, 2vw, 16px) clamp(14px, 2.5vw, 18px);
  margin-bottom: clamp(8px, 1.2vw, 12px);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(5px);
  animation: slideInFromLeft 0.6s ease-out calc(0.7s + var(--delay, 0s)) both;
}

.poll-option:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px) scale(1.02);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.poll-option.selected {
  border-color: #00b894;
  background: rgba(0, 184, 148, 0.2);
  box-shadow: 0 4px 16px rgba(0, 184, 148, 0.3);
}

.poll-option.winner {
  border-color: #ffd700;
  background: rgba(255, 215, 0, 0.15);
  box-shadow: 0 6px 24px rgba(255, 215, 0, 0.3);
}

.poll-option.disabled {
  cursor: default;
}

.option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.option-text {
  font-size: clamp(13px, 1.8vw, 16px);
  font-weight: 500;
}

.option-percentage {
  font-size: clamp(11px, 1.5vw, 14px);
  font-weight: 700;
}

.option-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 0 0 6px 6px;
}

.option-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 0 0 6px 6px;
  transition: width 1s ease;
}

.poll-footer {
  text-align: center;
}

.poll-submit-btn {
  background: linear-gradient(135deg, #00b894 0%, #00a085 100%);
  color: white;
  border: none;
  padding: clamp(12px, 2vw, 16px) clamp(20px, 4vw, 32px);
  border-radius: 25px;
  font-weight: 700;
  font-size: clamp(14px, 2vw, 18px);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 
    0 6px 20px rgba(0, 184, 148, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.poll-submit-btn:hover {
  background: linear-gradient(135deg, #00a085 0%, #00b894 100%);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 
    0 10px 30px rgba(0, 184, 148, 0.5),
    0 4px 12px rgba(0, 0, 0, 0.15);
}

.poll-votes {
  font-size: clamp(11px, 1.5vw, 14px);
  opacity: 0.8;
}

/* Carousel controls */
.poll-carousel ::v-deep .carousel-inner {
  background-color: transparent !important;
  height: 100%;
}

.poll-carousel ::v-deep .carousel-item {
  height: 100%;
}

.poll-carousel ::v-deep .carousel-control-prev,
.poll-carousel ::v-deep .carousel-control-next {
  background: rgba(255, 255, 255, 0.2);
  width: 40px;
  border-radius: 4px;
}

.poll-carousel ::v-deep .carousel-indicators {
  bottom: 10px;
}

.poll-carousel ::v-deep .carousel-indicators li {
  background-color: rgba(255, 255, 255, 0.5);
}

.poll-carousel ::v-deep .carousel-indicators .active {
  background-color: rgba(255, 255, 255, 0.9);
}

/* Responsive carousel controls based on space level */
.poll-feature-container.minimal ::v-deep .carousel-indicators {
  display: none;
}

.poll-feature-container.minimal ::v-deep .carousel-control-prev,
.poll-feature-container.minimal ::v-deep .carousel-control-next {
  width: 30px;
  opacity: 0.7;
}

.poll-feature-container.limited ::v-deep .carousel-control-prev,
.poll-feature-container.limited ::v-deep .carousel-control-next {
  width: 35px;
}

.poll-feature-container.normal ::v-deep .carousel-control-prev,
.poll-feature-container.normal ::v-deep .carousel-control-next {
  width: 40px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .poll-slide-container {
    padding: 15px;
  }
  
  .poll-title {
    font-size: 16px;
  }
  
  .poll-question {
    font-size: 13px;
  }
  
  .option-text {
    font-size: 12px;
  }
  
  .poll-feature-container ::v-deep .carousel-control-prev,
  .poll-feature-container ::v-deep .carousel-control-next {
    width: 30px;
  }
}

/* Background elements and animations */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  animation: floatAndPulse 8s ease-in-out infinite;
}

.circle-1 {
  width: 80px;
  height: 80px;
  top: 10%;
  right: 15%;
  animation-delay: 0s;
}

.circle-2 {
  width: 60px;
  height: 60px;
  bottom: 20%;
  left: 10%;
  animation-delay: 4s;
}

.progress-ring-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.03) 0%, transparent 70%);
  animation: slowRotate 20s linear infinite;
}

/* Poll icon with progress ring */
.poll-icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  animation: slideInFromTop 0.8s ease-out 0.1s both;
}

.poll-icon {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.vote-count-ring {
  position: absolute;
  top: -2px;
  left: -2px;
}

.progress-ring-circle {
  transition: stroke-dashoffset 0.8s ease-in-out;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}

.progress-ring-progress {
  transition: stroke-dashoffset 1.5s ease-in-out;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  stroke-linecap: round;
  filter: drop-shadow(0 0 4px rgba(255, 107, 107, 0.5));
}

/* Enhanced option styling */
.option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.option-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.option-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.poll-option.selected .option-indicator {
  background: #00b894;
  box-shadow: 0 0 8px rgba(0, 184, 148, 0.6);
}

.poll-option.winner .option-indicator {
  background: #ffd700;
  box-shadow: 0 0 8px rgba(255, 215, 0, 0.6);
}

.option-text {
  font-size: clamp(14px, 2vw, 18px);
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.option-percentage {
  font-size: clamp(12px, 1.8vw, 16px);
  font-weight: 800;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  backdrop-filter: blur(5px);
}

.option-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0 0 10px 10px;
  overflow: hidden;
}

.option-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 100%);
  border-radius: 0 0 10px 10px;
  transition: width 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 0 8px rgba(255, 107, 107, 0.4);
}

.winner-fill {
  background: linear-gradient(90deg, #ffd700 0%, #ffed4e 100%);
  box-shadow: 0 0 12px rgba(255, 215, 0, 0.6);
}

/* Enhanced footer styling */
.poll-completion {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #00b894;
  font-weight: 600;
  animation: slideInFromBottom 0.8s ease-out 0.3s both;
}

.poll-completion i {
  font-size: 20px;
  animation: checkmarkPulse 2s ease-in-out infinite;
}

/* Glassmorphism overlay */
.glass-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 16px;
  pointer-events: none;
  z-index: 1;
}

/* Enhanced hover effects */
.poll-content:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 
    0 20px 50px rgba(255, 118, 117, 0.5),
    0 10px 25px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.poll-content:hover .background-elements .bg-circle {
  animation-duration: 4s;
}

.poll-content:hover .progress-ring-bg {
  animation-duration: 10s;
}

/* Keyframe animations */
@keyframes gradientShift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes floatAndPulse {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.05;
  }
  50% {
    transform: translateY(-15px) scale(1.1);
    opacity: 0.15;
  }
}

@keyframes slowRotate {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes checkmarkPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* Add staggered animation delays for options */
.poll-option:nth-child(1) { --delay: 0.1s; }
.poll-option:nth-child(2) { --delay: 0.2s; }
.poll-option:nth-child(3) { --delay: 0.3s; }
.poll-option:nth-child(4) { --delay: 0.4s; }

/* Respect reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .poll-content,
  .bg-circle,
  .progress-ring-bg,
  .poll-icon,
  .option-fill {
    animation: none;
  }
  
  .poll-content:hover {
    transform: none;
  }
}

/* Emergency fallback for very small screens */
@media (max-height: 500px) {
  .poll-feature-container ::v-deep .carousel-indicators {
    display: none !important;
  }
  
  .poll-feature-container ::v-deep .carousel-control-prev,
  .poll-feature-container ::v-deep .carousel-control-next {
    width: 25px !important;
    opacity: 0.8 !important;
  }
  
  .poll-icon-container {
    margin-bottom: 12px;
  }
  
  .poll-icon {
    width: 44px;
    height: 44px;
    font-size: 22px;
  }
}
</style>